import React, { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { updateRatingsThresholds } from 'store/user/actions';
import { ParentalControlRating } from 'store/user/reducer.types';
import {
    selectIsNonInteractiveUI,
    selectParentalControlRating,
} from 'store/user/selectors';

import { COLOR } from 'utils/colors';
import useEnterTheApp from 'utils/hooks/appBootstrap/useEnterTheApp';
import { isWeb } from 'utils/responsiveness';
import { applyBoldStyle, applyNewLine } from 'utils/strings';

import DialogBody from 'components/Dialog/components/DialogBody';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

const {
    FTI_SUCCESSFUL_PIN_TITLE,
    FTI_SUCCESSFUL_PIN_DESCRIPTION,
    DONE_BUTTON,
} = LANGUAGE_KEYS;

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.PIN_SETUP_SUCCESS
    >;
};

export default function PinSetupSuccess({ navigation }: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { onEnterTheApp } = useEnterTheApp();

    const isNonInteractive = useSelector(selectIsNonInteractiveUI);
    const parentalRating = useSelector(selectParentalControlRating);

    const onPressContinue = useCallback(async () => {
        if (!isNonInteractive) {
            navigation.reset({
                routes: [{ name: SCREEN.FTI_TUTORIAL }],
            });
        } else {
            await onEnterTheApp();
        }
    }, [isNonInteractive, onEnterTheApp]);

    useEffect(() => {
        if (!parentalRating) {
            dispatch(updateRatingsThresholds(ParentalControlRating.HU16));
        }
    }, [dispatch, parentalRating]);

    return (
        <DialogBody
            title={FTI_SUCCESSFUL_PIN_TITLE}
            DescriptionComponent={() => (
                <Text
                    center
                    category={ETextCategory.body1}
                    color={ETextColor.light_grey}
                    style={styles.description}
                    {...(isWeb && { tabIndex: 0 })}
                >
                    {applyBoldStyle(
                        applyNewLine(t(FTI_SUCCESSFUL_PIN_DESCRIPTION)),
                        styles.description,
                    )}
                </Text>
            )}
            actionButtonTitle={DONE_BUTTON}
            actionButtonStyle={styles.button}
            actionButtonTextStyle={styles.buttonText}
            handleActionButtonPress={onPressContinue}
            actionButtonFillColor={COLOR.primary}
            style={styles.container}
        />
    );
}
